import React from "react";
import styled from "styled-components";
import { Helmet } from "react-helmet";
import StartUpHero from "../../assets/startup.jpg";
import AttachmentOne from "../../assets/attachment1.jpg";
import { GiPathDistance, GiHeavyThornyTriskelion } from "react-icons/gi";
import { RxExclamationTriangle } from "react-icons/rx";

const StartUps = () => {
  const handleTalkToTeam = () => {
    window.location.href = "/contact-us";
  };

  return (
    <>
      <Helmet>
        <title>Medichy | StartUps</title>
        <meta
          name="description"
          content="Medichy is a modern digital medical and healthcare platform delivering reliable medical and healthcare solutions."
        />
      </Helmet>
      <TheStartUps>
        <div className="contact-hero-section">
          <img src={StartUpHero} alt="page banner" />
          <div className="overlay"></div>
          <div className="hero-content">
            <h3>
              We can bring your medical device idea to life with accuracy, speed
              and precision.
            </h3>
            <p>
              Medichy guides medtech startups through design, feasibility, and
              regulatory complexity, giving you confidence to progress without
              costly mistakes.
            </p>
            <button className="btn-secondary" onClick={handleTalkToTeam}>
              TALK TO OUR TEAM
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="20"
                height="20"
                fill="currentColor"
                viewBox="0 0 16 16"
              >
                <path
                  fillRule="evenodd"
                  d="M1 8a.5.5 0 0 1 .5-.5h11.793l-3.147-3.146a.5.5 0 0 1 .708-.708l4 4a.5.5 0 0 1 0 .708l-4 4a.5.5 0 0 1-.708-.708L13.293 8.5H1.5A.5.5 0 0 1 1 8z"
                />
              </svg>
            </button>
          </div>
        </div>

        <div className="container">
          <div className="early-stage">
            <div className="section-header">
              <h3>Where early-stage teams get stuck</h3>
              <p className="section-subtitle">
                Early-stage medtech teams often move fast but lack clarity on
                design, feasibility, and regulatory pathways. This creates risk
                and wasted capital.
              </p>
            </div>

            <div className="imagery-container">
              <div className="imagery-image">
                <img src={AttachmentOne} alt="imagery-img" />
                <div className="image-overlay"></div>
              </div>

              <div className="imagery-content">
                <div className="content-item">
                  <div className="icon-wrapper">
                    <GiPathDistance className="icon" />
                  </div>
                  <div className="item-text">
                    <h4>Uncertainty on FDA/CE pathway</h4>
                    <p>
                      Navigating regulatory requirements without expert guidance
                      leads to delays and compliance issues.
                    </p>
                  </div>
                </div>

                <div className="content-item">
                  <div className="icon-wrapper">
                    <GiHeavyThornyTriskelion className="icon" />
                  </div>
                  <div className="item-text">
                    <h4>Risk of wasted spend on the wrong design</h4>
                    <p>
                      Investing in designs that don't meet regulatory or market
                      needs results in costly rework.
                    </p>
                  </div>
                </div>

                <div className="content-item">
                  <div className="icon-wrapper">
                    <RxExclamationTriangle className="icon" />
                  </div>
                  <div className="item-text">
                    <h4>Struggle to move from idea to working prototype</h4>
                    <p>
                      Turning concepts into functional prototypes requires
                      specialized expertise and resources.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="prototype-sprint-section">
            <div className="section-header">
              <h3>Your first step: The Prototype Sprint</h3>
              <p className="section-subtitle">
                The Prototype Sprint is a structured, fixed-scope engagement
                that delivers a manufacturable prototype and regulatory clarity.
                It gives you momentum and confidence to move forward.
              </p>
              <button className="sprint-explore-btn" onClick={handleTalkToTeam}>
                Explore The Sprint
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="20"
                  height="20"
                  fill="currentColor"
                  viewBox="0 0 16 16"
                >
                  <path
                    fillRule="evenodd"
                    d="M1 8a.5.5 0 0 1 .5-.5h11.793l-3.147-3.146a.5.5 0 0 1 .708-.708l4 4a.5.5 0 0 1 0 .708l-4 4a.5.5 0 0 1-.708-.708L13.293 8.5H1.5A.5.5 0 0 1 1 8z"
                  />
                </svg>
              </button>
            </div>

            <div className="sprint-timeline">
              <div className="timeline-container">
                <div className="timeline-path">
                  <div className="timeline-line"></div>
                  <div className="timeline-dots">
                    {[1, 2, 3, 4, 5, 6].map((dot) => (
                      <div key={dot} className="timeline-dot"></div>
                    ))}
                  </div>
                </div>

                <div className="timeline-cards">
                  <div className="timeline-card">
                    <div className="card-header">
                      <div className="card-number">01</div>
                      <div className="card-phase">Requirements</div>
                    </div>
                    <div className="card-history">
                      <div className="history-icon">
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="16"
                          height="16"
                          fill="currentColor"
                          viewBox="0 0 16 16"
                        >
                          <path d="M14 1a1 1 0 0 1 1 1v12a1 1 0 0 1-1 1H2a1 1 0 0 1-1-1V2a1 1 0 0 1 1-1h12zM2 0a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V2a2 2 0 0 0-2-2H2z" />
                          <path d="M9.5 13a.5.5 0 0 1-.5-.5V5.707L5.354 7.854a.5.5 0 1 1-.708-.708l3-3a.5.5 0 0 1 .708 0l3 3a.5.5 0 0 1-.708.708L10 5.707V12.5a.5.5 0 0 1-.5.5z" />
                        </svg>
                      </div>
                      <span className="history-text">History Point</span>
                    </div>
                    <h4 className="card-title">Define User Requirements</h4>
                    <p className="card-description">
                      Identify and document user needs, clinical requirements,
                      and technical specifications.
                    </p>
                  </div>

                  <div className="timeline-card">
                    <div className="card-header">
                      <div className="card-number">02</div>
                      <div className="card-phase">Concept</div>
                    </div>
                    <div className="card-history">
                      <div className="history-icon">
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="16"
                          height="16"
                          fill="currentColor"
                          viewBox="0 0 16 16"
                        >
                          <path d="M14 1a1 1 0 0 1 1 1v12a1 1 0 0 1-1 1H2a1 1 0 0 1-1-1V2a1 1 0 0 1 1-1h12zM2 0a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V2a2 2 0 0 0-2-2H2z" />
                          <path d="M9.5 13a.5.5 0 0 1-.5-.5V5.707L5.354 7.854a.5.5 0 1 1-.708-.708l3-3a.5.5 0 0 1 .708 0l3 3a.5.5 0 0 1-.708.708L10 5.707V12.5a.5.5 0 0 1-.5.5z" />
                        </svg>
                      </div>
                      <span className="history-text">History Point</span>
                    </div>
                    <h4 className="card-title">Group Ideation</h4>
                    <p className="card-description">
                      Collaborative brainstorming sessions to generate multiple
                      design concepts and approaches.
                    </p>
                  </div>

                  <div className="timeline-card">
                    <div className="card-header">
                      <div className="card-number">03</div>
                      <div className="card-phase">Development</div>
                    </div>
                    <div className="card-history">
                      <div className="history-icon">
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="16"
                          height="16"
                          fill="currentColor"
                          viewBox="0 0 16 16"
                        >
                          <path d="M14 1a1 1 0 0 1 1 1v12a1 1 0 0 1-1 1H2a1 1 0 0 1-1-1V2a1 1 0 0 1 1-1h12zM2 0a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V2a2 2 0 0 0-2-2H2z" />
                          <path d="M9.5 13a.5.5 0 0 1-.5-.5V5.707L5.354 7.854a.5.5 0 1 1-.708-.708l3-3a.5.5 0 0 1 .708 0l3 3a.5.5 0 0 1-.708.708L10 5.707V12.5a.5.5 0 0 1-.5.5z" />
                        </svg>
                      </div>
                      <span className="history-text">History Point</span>
                    </div>
                    <h4 className="card-title">Select Designs</h4>
                    <p className="card-description">
                      Evaluate and select the most promising designs based on
                      feasibility and requirements.
                    </p>
                  </div>

                  <div className="timeline-card">
                    <div className="card-header">
                      <div className="card-number">04</div>
                      <div className="card-phase">1st Prototype</div>
                    </div>
                    <div className="card-history">
                      <div className="history-icon">
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="16"
                          height="16"
                          fill="currentColor"
                          viewBox="0 0 16 16"
                        >
                          <path d="M14 1a1 1 0 0 1 1 1v12a1 1 0 0 1-1 1H2a1 1 0 0 1-1-1V2a1 1 0 0 1 1-1h12zM2 0a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V2a2 2 0 0 0-2-2H2z" />
                          <path d="M9.5 13a.5.5 0 0 1-.5-.5V5.707L5.354 7.854a.5.5 0 1 1-.708-.708l3-3a.5.5 0 0 1 .708 0l3 3a.5.5 0 0 1-.708.708L10 5.707V12.5a.5.5 0 0 1-.5.5z" />
                        </svg>
                      </div>
                      <span className="history-text">History Point</span>
                    </div>
                    <h4 className="card-title">Produce Physical Models</h4>
                    <p className="card-description">
                      Create initial physical prototypes for testing and
                      validation with stakeholders.
                    </p>
                  </div>

                  <div className="timeline-card">
                    <div className="card-header">
                      <div className="card-number">05</div>
                      <div className="card-phase">Refinement</div>
                    </div>
                    <div className="card-history">
                      <div className="history-icon">
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="16"
                          height="16"
                          fill="currentColor"
                          viewBox="0 0 16 16"
                        >
                          <path d="M14 1a1 1 0 0 1 1 1v12a1 1 0 0 1-1 1H2a1 1 0 0 1-1-1V2a1 1 0 0 1 1-1h12zM2 0a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V2a2 2 0 0 0-2-2H2z" />
                          <path d="M9.5 13a.5.5 0 0 1-.5-.5V5.707L5.354 7.854a.5.5 0 1 1-.708-.708l3-3a.5.5 0 0 1 .708 0l3 3a.5.5 0 0 1-.708.708L10 5.707V12.5a.5.5 0 0 1-.5.5z" />
                        </svg>
                      </div>
                      <span className="history-text">History Point</span>
                    </div>
                    <h4 className="card-title">Select One Design</h4>
                    <p className="card-description">
                      Based on testing results, choose the optimal design for
                      final development.
                    </p>
                  </div>

                  <div className="timeline-card">
                    <div className="card-header">
                      <div className="card-number">06</div>
                      <div className="card-phase">2nd Prototype</div>
                    </div>
                    <div className="card-history">
                      <div className="history-icon">
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="16"
                          height="16"
                          fill="currentColor"
                          viewBox="0 0 16 16"
                        >
                          <path d="M14 1a1 1 0 0 1 1 1v12a1 1 0 0 1-1 1H2a1 1 0 0 1-1-1V2a1 1 0 0 1 1-1h12zM2 0a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V2a2 2 0 0 0-2-2H2z" />
                          <path d="M9.5 13a.5.5 0 0 1-.5-.5V5.707L5.354 7.854a.5.5 0 1 1-.708-.708l3-3a.5.5 0 0 1 .708 0l3 3a.5.5 0 0 1-.708.708L10 5.707V12.5a.5.5 0 0 1-.5.5z" />
                        </svg>
                      </div>
                      <span className="history-text">History Point</span>
                    </div>
                    <h4 className="card-title">Based On Refined Design</h4>
                    <p className="card-description">
                      Create the final manufacturable prototype ready for
                      regulatory submission.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="services-section">
            <div className="section-header">
              <h3>How Medichy Helps Startups</h3>
              <p className="section-subtitle">
                Our comprehensive approach ensures your medical device startup
                navigates every stage successfully.
              </p>
            </div>

            <div className="services-grid">
              <div className="service-card">
                <div className="service-icon">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="32"
                    height="32"
                    fill="currentColor"
                    viewBox="0 0 16 16"
                  >
                    <path d="M9.05.435c-.58-.58-1.52-.58-2.1 0L.436 6.95c-.58.58-.58 1.519 0 2.098l6.516 6.516c.58.58 1.519.58 2.098 0l6.516-6.516c.58-.58.58-1.519 0-2.098L9.05.435zM8 4a.905.905 0 0 0-.9.995l.35 3.507a.552.552 0 0 0 1.1 0l.35-3.507A.905.905 0 0 0 8 4zm.002 6a1 1 0 1 0 0 2 1 1 0 0 0 0-2z" />
                  </svg>
                </div>
                <h4>Risk Assessment</h4>
                <p>
                  Identify potential regulatory and technical risks early to
                  avoid costly mistakes and delays.
                </p>
              </div>

              <div className="service-card">
                <div className="service-icon">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="32"
                    height="32"
                    fill="currentColor"
                    viewBox="0 0 16 16"
                  >
                    <path d="M8.515 1.019A7 7 0 0 0 8 1V0a8 8 0 0 1 .589.022l-.074.997zm2.004.45a7.003 7.003 0 0 0-.985-.299l.219-.976c.383.086.76.2 1.126.342l-.36.933zm1.37.71a7.01 7.01 0 0 0-.439-.27l.493-.87a8.025 8.025 0 0 1 .979.654l-.615.789a6.996 6.996 0 0 0-.418-.302zm1.834 1.79a6.99 6.99 0 0 0-.653-.796l.724-.69c.27.285.52.59.747.91l-.818.576zm.744 1.352a7.08 7.08 0 0 0-.214-.468l.893-.45a7.976 7.976 0 0 1 .45 1.088l-.95.313a7.023 7.023 0 0 0-.179-.483zm.53 2.507a6.991 6.991 0 0 0-.1-1.025l.985-.17c.067.386.106.778.116 1.17l-1.025zm-.131 1.538c.033-.17.06-.339.081-.51l.993.123a7.957 7.957 0 0 1-.23 1.155l-.964-.267c.046-.165.086-.332.12-.501zm-.952 2.379c.184-.29.346-.594.486-.908l.914.405c-.16.36-.345.706-.555 1.038l-.845-.535zm-.964 1.205c.122-.122.239-.248.35-.378l.758.653a8.073 8.073 0 0 1-.401.432l-.707-.707z" />
                    <path d="M8 1a7 7 0 1 0 4.95 11.95l.707.707A8.001 8.001 0 1 1 8 0v1z" />
                    <path d="M7.5 3a.5.5 0 0 1 .5.5v5.21l3.248 1.856a.5.5 0 0 1-.496.868l-3.5-2A.5.5 0 0 1 7 9V3.5a.5.5 0 0 1 .5-.5z" />
                  </svg>
                </div>
                <h4>Regulatory Strategy</h4>
                <p>
                  Develop clear FDA/CE pathways and compliance plans tailored to
                  your specific device.
                </p>
              </div>

              <div className="service-card">
                <div className="service-icon">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="32"
                    height="32"
                    fill="currentColor"
                    viewBox="0 0 16 16"
                  >
                    <path d="M2 1a1 1 0 0 0-1 1v4.586a1 1 0 0 0 .293.707l7 7a1 1 0 0 0 1.414 0l4.586-4.586a1 1 0 0 0 0-1.414l-7-7A1 1 0 0 0 6.586 1H2zm4 3.5a1.5 1.5 0 1 1-3 0 1.5 1.5 0 0 1 3 0z" />
                  </svg>
                </div>
                <h4>Prototype Development</h4>
                <p>
                  Turn your ideas into working prototypes with our expert design
                  and engineering team.
                </p>
              </div>

              <div className="service-card">
                <div className="service-icon">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="32"
                    height="32"
                    fill="currentColor"
                    viewBox="0 0 16 16"
                  >
                    <path d="M8 15A7 7 0 1 1 8 1a7 7 0 0 1 0 14zm0 1A8 8 0 1 0 8 0a8 8 0 0 0 0 16z" />
                    <path d="m8.93 6.588-2.29.287-.082.38.45.083c.294.07.352.176.288.469l-.738 3.468c-.194.897.105 1.319.808 1.319.545 0 1.178-.252 1.465-.598l.088-.416c-.2.176-.492.246-.686.246-.275 0-.375-.193-.304-.533L8.93 6.588zM9 4.5a1 1 0 1 1-2 0 1 1 0 0 1 2 0z" />
                  </svg>
                </div>
                <h4>Feasibility Studies</h4>
                <p>
                  Validate your concepts with comprehensive market and technical
                  feasibility analysis.
                </p>
              </div>
            </div>
          </div>

          <div className="cta-section">
            <div className="cta-content">
              <h3>Ready to transform your medical device idea?</h3>
              <p>
                Schedule a free consultation with our experts to discuss your
                project requirements.
              </p>
              <button className="btn-primary" onClick={handleTalkToTeam}>
                GET STARTED TODAY
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="20"
                  height="20"
                  fill="currentColor"
                  viewBox="0 0 16 16"
                >
                  <path
                    fillRule="evenodd"
                    d="M1 8a.5.5 0 0 1 .5-.5h11.793l-3.147-3.146a.5.5 0 0 1 .708-.708l4 4a.5.5 0 0 1 0 .708l-4 4a.5.5 0 0 1-.708-.708L13.293 8.5H1.5A.5.5 0 0 1 1 8z"
                  />
                </svg>
              </button>
            </div>
          </div>
        </div>
      </TheStartUps>
    </>
  );
};

const TheStartUps = styled.div`
  width: 100%;
  min-height: 100vh;
  overflow-x: hidden; 

  .container {
    max-width: 1200px;
    margin: 0 auto;
    padding: 0 2rem;
  }

  .contact-hero-section {
    position: relative;
    height: 70vh;
    width: 100%;
    overflow: hidden;
    margin-bottom: 4rem;

    img {
      width: 100%;
      height: 100%;
      object-fit: cover;
      object-position: center;
    }

    .overlay {
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      background: linear-gradient(
        135deg,
        rgba(0, 100, 200, 0.85) 0%,
        rgba(0, 50, 150, 0.9) 100%
      );
    }

    .hero-content {
      position: absolute;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      text-align: center;
      color: #fff;
      width: 90%;
      max-width: 900px;
      padding: 0 1rem;

      h3 {
        font-size: 2.5rem;
        font-weight: 700;
        margin-bottom: 1.5rem;
        text-shadow: 2px 2px 8px rgba(0, 0, 0, 0.3);
        line-height: 1;
      }

      p {
        font-size: 1rem;
        font-weight: 400;
        opacity: 0.95;
        text-shadow: 1px 1px 2px rgba(0, 0, 0, 0.3);
        margin-bottom: 2rem;
        line-height: 1.6;
        max-width: 700px;
        margin-left: auto;
        margin-right: auto;
      }

      .btn-secondary {
        display: inline-flex;
        align-items: center;
        gap: 0.75rem;
        background: transparent;
        color: white;
        border: 2px solid white;
        padding: 1rem 2.5rem;
        font-size: 1.1rem;
        font-weight: 600;
        border-radius: 50px;
        cursor: pointer;
        transition: all 0.3s ease;
        text-transform: uppercase;
        letter-spacing: 0.5px;

        &:hover {
          background: white;
          color: #3182ce;
          transform: translateY(-2px);
          box-shadow: 0 4px 15px rgba(255, 255, 255, 0.2);

          svg {
            transform: translateX(5px);
          }
        }

        svg {
          transition: transform 0.3s ease;
        }
      }
    }
  }

  .section-header {
    text-align: center;
    margin-bottom: 4rem;

    h3 {
      font-size: 2.5rem;
      font-weight: 700;
      color: #1a1a1a;
      margin-bottom: 1rem;
    }

    .section-subtitle {
      font-size: 1.25rem;
      color: #666;
      line-height: 1.6;
      max-width: 800px;
      margin: 0 auto 2rem;
    }
  }

  .early-stage {
    padding: 4rem 0;

    .imagery-container {
      display: grid;
      grid-template-columns: 1fr 1fr;
      gap: 4rem;
      align-items: center;
      margin-top: 3rem;
    }

    .imagery-image {
      position: relative;
      border-radius: 16px;
      overflow: hidden;
      box-shadow: 0 8px 30px rgba(0, 0, 0, 0.1);
      height: 400px;

      img {
        width: 100%;
        height: 100%;
        object-fit: cover;
      }

      .image-overlay {
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        background: linear-gradient(
          to right,
          rgba(0, 100, 200, 0.1),
          transparent
        );
      }
    }

    .imagery-content {
      .content-item {
        display: flex;
        align-items: flex-start;
        gap: 1.5rem;
        margin-bottom: 2.5rem;
        padding: 1.5rem;
        border-radius: 12px;
        background: #f8fafc;
        transition: all 0.3s ease;

        &:hover {
          background: #edf2f7;
          transform: translateX(10px);
        }

        &:last-child {
          margin-bottom: 0;
        }

        .icon-wrapper {
          width: 60px;
          height: 60px;
          background: linear-gradient(135deg, #3182ce, #2c5282);
          border-radius: 12px;
          display: flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;

          .icon {
            color: white;
            font-size: 1.75rem;
          }
        }

        .item-text {
          h4 {
            font-size: 1.25rem;
            font-weight: 600;
            color: #1a1a1a;
            margin-bottom: 0.5rem;
          }

          p {
            font-size: 1rem;
            color: #666;
            line-height: 1.5;
          }
        }
      }
    }
  }

  .prototype-sprint-section {
    padding: 6rem 0;
    background: linear-gradient(135deg, #f8fafc 0%, #edf2f7 100%);
    border-radius: 24px;
    margin: 4rem 0;
    position: relative;
    overflow: hidden; /* Prevent timeline overflow */

    .section-header {
      margin-bottom: 3rem;

      .sprint-explore-btn {
        display: inline-flex;
        align-items: center;
        gap: 0.75rem;
        background: linear-gradient(135deg, #3182ce 0%, #2c5282 100%);
        color: white;
        border: none;
        padding: 1rem 2.5rem;
        font-size: 1.1rem;
        font-weight: 600;
        border-radius: 50px;
        cursor: pointer;
        transition: all 0.3s ease;
        text-transform: uppercase;
        letter-spacing: 0.5px;
        margin-top: 1rem;

        &:hover {
          transform: translateY(-2px);
          box-shadow: 0 8px 25px rgba(49, 130, 206, 0.4);

          svg {
            transform: translateX(5px);
          }
        }

        svg {
          transition: transform 0.3s ease;
        }
      }
    }

    .sprint-timeline {
      position: relative;
      width: 100%;
      padding: 2rem 0;

      .timeline-container {
        position: relative;
        width: 100%;
        max-width: 1200px;
        margin: 0 auto;
      }

      .timeline-path {
        position: absolute;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);
        width: 90%;
        height: 4px;
        z-index: 1;

        .timeline-line {
          width: 100%;
          height: 100%;
          background: linear-gradient(90deg, #3182ce, #2c5282);
          border-radius: 2px;
        }

        .timeline-dots {
          position: absolute;
          top: 50%;
          left: 0;
          right: 0;
          transform: translateY(-50%);
          display: flex;
          justify-content: space-between;

          .timeline-dot {
            width: 16px;
            height: 16px;
            background: white;
            border: 3px solid #3182ce;
            border-radius: 50%;
            position: relative;
            z-index: 2;
            flex-shrink: 0;
          }
        }
      }

      .timeline-cards {
        display: grid;
        grid-template-columns: repeat(3, 1fr);
        gap: 2rem;
        position: relative;
        z-index: 3;
        width: 100%;

        .timeline-card {
          background: white;
          border-radius: 16px;
          padding: 2rem;
          box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
          transition: all 0.3s ease;
          text-align: center;
          border: 2px solid transparent;
          width: 100%;
          box-sizing: border-box; /* Include padding in width calculation */

          &:hover {
            transform: translateY(-8px);
            box-shadow: 0 8px 30px rgba(0, 0, 0, 0.12);
            border-color: #3182ce;
          }

          .card-header {
            display: flex;
            align-items: center;
            justify-content: center;
            gap: 0.75rem;
            margin-bottom: 1rem;

            .card-number {
              font-size: 2rem;
              font-weight: 700;
              color: #3182ce;
              line-height: 1;
            }

            .card-phase {
              font-size: 1.1rem;
              font-weight: 600;
              color: #1a1a1a;
              text-transform: uppercase;
              letter-spacing: 0.5px;
            }
          }

          .card-history {
            display: flex;
            align-items: center;
            justify-content: center;
            gap: 0.5rem;
            margin-bottom: 1rem;
            color: #666;
            font-size: 0.9rem;

            .history-icon {
              display: flex;
              align-items: center;
              justify-content: center;

              svg {
                color: #3182ce;
              }
            }

            .history-text {
              font-weight: 500;
            }
          }

          .card-title {
            font-size: 1.25rem;
            font-weight: 600;
            color: #1a1a1a;
            margin-bottom: 1rem;
            line-height: 1.3;
          }

          .card-description {
            font-size: 1rem;
            color: #666;
            line-height: 1.5;
            word-wrap: break-word;
            overflow-wrap: break-word;
          }
        }
      }
    }
  }

  .services-section {
    padding: 4rem 0;
    background: #f8fafc;
    border-radius: 24px;
    margin: 4rem 0;

    .services-grid {
      display: grid;
      grid-template-columns: repeat(4, 1fr);
      gap: 2rem;
      margin-top: 3rem;
    }

    .service-card {
      background: white;
      border-radius: 16px;
      padding: 2rem;
      box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
      transition: all 0.3s ease;
      text-align: center;

      &:hover {
        transform: translateY(-8px);
        box-shadow: 0 8px 30px rgba(0, 0, 0, 0.12);
      }

      .service-icon {
        width: 70px;
        height: 70px;
        background: linear-gradient(135deg, #3182ce, #2c5282);
        border-radius: 50%;
        display: flex;
        align-items: center;
        justify-content: center;
        margin: 0 auto 1.5rem;

        svg {
          color: white;
          font-size: 1.75rem;
        }
      }

      h4 {
        font-size: 1.25rem;
        font-weight: 600;
        color: #1a1a1a;
        margin-bottom: 1rem;
      }

      p {
        font-size: 1rem;
        color: #666;
        line-height: 1.5;
      }
    }
  }

  .cta-section {
    padding: 5rem 0;
    text-align: center;

    .cta-content {
      max-width: 800px;
      margin: 0 auto;

      h3 {
        font-size: 2.5rem;
        font-weight: 700;
        color: #1a1a1a;
        margin-bottom: 1rem;
      }

      p {
        font-size: 1.25rem;
        color: #666;
        line-height: 1.6;
        margin-bottom: 2.5rem;
      }

      .btn-primary {
        display: inline-flex;
        align-items: center;
        gap: 0.75rem;
        background: linear-gradient(135deg, #3182ce 0%, #2c5282 100%);
        color: white;
        border: none;
        padding: 1.25rem 3rem;
        font-size: 1.1rem;
        font-weight: 600;
        border-radius: 50px;
        cursor: pointer;
        transition: all 0.3s ease;
        text-transform: uppercase;
        letter-spacing: 0.5px;

        &:hover {
          transform: translateY(-2px);
          box-shadow: 0 8px 25px rgba(49, 130, 206, 0.4);

          svg {
            transform: translateX(5px);
          }
        }

        svg {
          transition: transform 0.3s ease;
        }
      }
    }
  }

  /* Media Queries */
  @media screen and (max-width: 1366px) {
    .contact-hero-section {
      height: 65vh;

      .hero-content {
        h3 {
          font-size: 3.25rem;
        }

        p {
          font-size: 1.2rem;
        }
      }
    }

    .container {
      padding: 0 1.5rem;
    }

    .section-header {
      h3 {
        font-size: 2.25rem;
      }
    }

    .prototype-sprint-section {
      .timeline-cards {
        gap: 1.5rem;

        .timeline-card {
          padding: 1.75rem;
        }
      }
    }
  }

  @media screen and (max-width: 1200px) {
    .contact-hero-section {
      height: 60vh;

      .hero-content {
        h3 {
          font-size: 3rem;
        }
      }
    }

    .early-stage {
      .imagery-container {
        gap: 3rem;
      }

      .imagery-image {
        height: 350px;
      }
    }

    .prototype-sprint-section {
      .timeline-cards {
        grid-template-columns: repeat(2, 1fr);
        gap: 2rem;
      }

      .timeline-path {
        top: 40%;
        width: 85%;

        .timeline-dots {
          .timeline-dot {
            &:nth-child(odd) {
              transform: translateY(-50px);
            }
            &:nth-child(even) {
              transform: translateY(50px);
            }
          }
        }
      }
    }

    .services-section {
      .services-grid {
        grid-template-columns: repeat(2, 1fr);
        gap: 1.5rem;
      }
    }
  }

  @media screen and (max-width: 900px) {
    .contact-hero-section {
      height: 55vh;
      margin-bottom: 3rem;

      .hero-content {
        h3 {
          font-size: 2.5rem;
        }

        p {
          font-size: 1.1rem;
          margin-bottom: 1.5rem;
        }

        .btn-secondary {
          padding: 0.875rem 2rem;
          font-size: 1rem;
        }
      }
    }

    .early-stage {
      padding: 3rem 0;

      .imagery-container {
        grid-template-columns: 1fr;
        gap: 3rem;
      }

      .imagery-image {
        height: 300px;
        order: 2;
      }

      .imagery-content {
        order: 1;
      }
    }

    .prototype-sprint-section {
      padding: 4rem 0;
      margin: 3rem 0;
      border-radius: 16px;

      .section-header {
        margin-bottom: 2.5rem;

        .sprint-explore-btn {
          padding: 0.875rem 2rem;
          font-size: 1rem;
        }
      }

      .sprint-timeline {
        padding: 1rem 0;

        .timeline-path {
          display: none; /* Hide timeline on mobile */
        }

        .timeline-cards {
          grid-template-columns: 1fr;
          max-width: 500px;
          margin: 0 auto;
          gap: 1.5rem;
        }
      }
    }

    .services-section {
      padding: 3rem 1.5rem;
      margin: 3rem 0;

      .services-grid {
        grid-template-columns: 1fr;
        max-width: 500px;
        margin: 3rem auto 0;
      }
    }

    .cta-section {
      padding: 4rem 0;

      .cta-content {
        h3 {
          font-size: 2rem;
        }

        p {
          font-size: 1.1rem;
        }

        .btn-primary {
          padding: 1rem 2.5rem;
          font-size: 1rem;
        }
      }
    }

    .section-header {
      margin-bottom: 3rem;

      h3 {
        font-size: 2rem;
      }

      .section-subtitle {
        font-size: 1.1rem;
      }
    }
  }

  @media screen and (max-width: 768px) {
    .prototype-sprint-section {
      .sprint-timeline {
        .timeline-cards {
          .timeline-card {
            margin: 0 auto;
            max-width: 100%;
          }
        }
      }
    }
  }

  @media screen and (max-width: 600px) {
    .contact-hero-section {
      height: 100vh;

      .hero-content {
        h3 {
          font-size: 2rem;
          margin-bottom: 1rem;
        }

        p {
          font-size: 1rem;
          margin-bottom: 1.5rem;
        }

        .btn-secondary {
          padding: 0.75rem 1.5rem;
          font-size: 0.9rem;
        }
      }
    }

    .container {
      padding: 0 1rem;
    }

    .early-stage {
      padding: 2.5rem 0;

      .imagery-image {
        height: 250px;
      }

      .imagery-content {
        .content-item {
          flex-direction: column;
          text-align: center;
          gap: 1rem;
          padding: 1.25rem;

          .icon-wrapper {
            margin: 0 auto;
          }

          &:hover {
            transform: translateY(-5px);
          }
        }
      }
    }

    .prototype-sprint-section {
      padding: 3rem 0;
      margin: 2.5rem 0;

      .sprint-timeline {
        .timeline-cards {
          .timeline-card {
            padding: 1.5rem;
            max-width: 95%;

            .card-header {
              .card-number {
                font-size: 1.75rem;
              }

              .card-phase {
                font-size: 1rem;
              }
            }

            .card-title {
              font-size: 1.1rem;
            }

            .card-description {
              font-size: 0.95rem;
            }
          }
        }
      }
    }

    .services-section {
      padding: 2.5rem 1rem;
      border-radius: 16px;

      .service-card {
        padding: 1.5rem;

        .service-icon {
          width: 60px;
          height: 60px;
          margin-bottom: 1rem;

          svg {
            font-size: 1.5rem;
          }
        }

        h4 {
          font-size: 1.1rem;
        }

        p {
          font-size: 0.95rem;
        }
      }
    }

    .cta-section {
      padding: 3rem 0;

      .cta-content {
        h3 {
          font-size: 1.75rem;
        }

        p {
          font-size: 1rem;
          margin-bottom: 2rem;
        }

        .btn-primary {
          padding: 0.875rem 2rem;
          font-size: 0.95rem;
        }
      }
    }

    .section-header {
      margin-bottom: 2.5rem;

      h3 {
        font-size: 1.75rem;
      }

      .section-subtitle {
        font-size: 1rem;
      }
    }
  }

  @media screen and (max-width: 480px) {
    .prototype-sprint-section {
      .sprint-timeline {
        .timeline-cards {
          .timeline-card {
            padding: 1.25rem;
            margin: 0 0.5rem;
          }
        }
      }
    }
  }

  @media screen and (max-width: 420px) {
    .contact-hero-section {
      height: 100vh;

      .hero-content {
        h3 {
          font-size: 1.75rem;
        }

        p {
          font-size: 0.9rem;
        }

        .btn-secondary {
          padding: 0.625rem 1.25rem;
          font-size: 0.85rem;
        }
      }
    }

    .early-stage {
      padding: 2rem 0;

      .imagery-image {
        height: 200px;
      }

      .imagery-content {
        .content-item {
          padding: 1rem;
          margin-bottom: 1.5rem;

          .icon-wrapper {
            width: 50px;
            height: 50px;

            .icon {
              font-size: 1.5rem;
            }
          }

          .item-text {
            h4 {
              font-size: 1.1rem;
            }

            p {
              font-size: 0.9rem;
            }
          }
        }
      }
    }

    .prototype-sprint-section {
      padding: 2.5rem 0.75rem;
      margin: 2rem 0;

      .sprint-timeline {
        .timeline-cards {
          .timeline-card {
            padding: 1.25rem;

            .card-header {
              .card-number {
                font-size: 1.5rem;
              }
            }

            .card-title {
              font-size: 1rem;
            }

            .card-description {
              font-size: 0.9rem;
            }
          }
        }
      }
    }

    .services-section {
      padding: 2rem 0.75rem;
      margin: 2rem 0;

      .service-card {
        padding: 1.25rem;

        .service-icon {
          width: 50px;
          height: 50px;

          svg {
            font-size: 1.25rem;
          }
        }

        h4 {
          font-size: 1rem;
        }

        p {
          font-size: 0.9rem;
        }
      }
    }

    .cta-section {
      padding: 2.5rem 0;

      .cta-content {
        h3 {
          font-size: 1.5rem;
        }

        p {
          font-size: 0.9rem;
        }

        .btn-primary {
          padding: 0.75rem 1.5rem;
          font-size: 0.9rem;
        }
      }
    }

    .section-header {
      h3 {
        font-size: 1.5rem;
      }

      .section-subtitle {
        font-size: 0.9rem;
      }
    }
  }

  @media screen and (max-width: 350px) {
    .contact-hero-section {
      height: 100vh;

      .hero-content {
        h3 {
          font-size: 1.5rem;
        }

        p {
          font-size: 0.85rem;
        }

        .btn-secondary {
          padding: 0.5rem 1rem;
          font-size: 0.8rem;

          svg {
            width: 16px;
            height: 16px;
          }
        }
      }
    }

    .container {
      padding: 0 0.5rem;
    }

    .early-stage {
      .imagery-image {
        height: 180px;
      }

      .imagery-content {
        .content-item {
          .icon-wrapper {
            width: 45px;
            height: 45px;

            .icon {
              font-size: 1.25rem;
            }
          }

          .item-text {
            h4 {
              font-size: 1rem;
            }

            p {
              font-size: 0.85rem;
            }
          }
        }
      }
    }

    .prototype-sprint-section {
      padding: 2rem 0.5rem;

      .sprint-timeline {
        .timeline-cards {
          .timeline-card {
            padding: 1rem;

            .card-header {
              .card-number {
                font-size: 1.25rem;
              }

              .card-phase {
                font-size: 0.9rem;
              }
            }

            .card-title {
              font-size: 0.95rem;
            }

            .card-description {
              font-size: 0.85rem;
            }
          }
        }
      }
    }

    .services-section {
      .service-card {
        padding: 1rem;

        .service-icon {
          width: 45px;
          height: 45px;

          svg {
            font-size: 1.1rem;
          }
        }

        h4 {
          font-size: 0.95rem;
        }

        p {
          font-size: 0.85rem;
        }
      }
    }

    .cta-section {
      padding: 2rem 0;

      .cta-content {
        h3 {
          font-size: 1.25rem;
        }

        p {
          font-size: 0.85rem;
        }

        .btn-primary {
          padding: 0.625rem 1.25rem;
          font-size: 0.85rem;

          svg {
            width: 16px;
            height: 16px;
          }
        }
      }
    }

    .section-header {
      h3 {
        font-size: 1.25rem;
      }

      .section-subtitle {
        font-size: 0.85rem;
      }
    }
  }
`;

export default StartUps;
